import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import isTomorrow from 'dayjs/plugin/isTomorrow';

import { CartKeys } from '@config/keys/cart.keys';

import { DoodCartModel } from '@core/models/cart.model';
import { ISimpleItem } from '@core/models/simple-item.model';

import { ParametersWantedAtTypeLabels } from '@config/labels/parameters.labels';
import { IOrderTypeCapabilities } from '@config/order-types-capabilities.config';
import { OrderTypeValues } from '@config/values/order.values';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { DateUtils } from '@shared/utils/date/date.utils';
import { DoodOrderModel } from '@store/order/order.model';

const DEFAULT_DELIVERY_DURATION_SECONDS = 900;

@Injectable({
  providedIn: 'root',
})
export class CartParametersService {
  private readonly orderTypeService = inject(OrderTypeService);
  private readonly translateService = inject(TranslateService);

  constructor() {
    dayjs.extend(isTomorrow);
  }

  retrieveCartParametersValue(
    cart: DoodCartModel,
    order?: DoodOrderModel | null,
    slots?: number[],
    validatedOrder?: {
      estimated_delivery_duration?: number | null;
      ready_at?: Date | string | null;
    } | null,
  ): ISimpleItem[] {
    const wantedAt = cart.wanted_at;
    const distributionMode = cart.type;

    const orderTypeCapabilities = this.orderTypeService.getCapabilities(distributionMode);
    const distributionIcon = orderTypeCapabilities.icon;
    let distributionLabel = orderTypeCapabilities.label;

    // Add table name to distribution label if onsite service
    if (orderTypeCapabilities.isOnSiteLocationMandatory) {
      const translatedDistributionLabel = this.translateService.instant(distributionLabel);
      const tableName = cart[CartKeys.OnSiteLocationName]
        ? (cart.on_site_location_name ?? '')
        : (this.translateService.instant('parameters.unknown-location') ?? '');
      distributionLabel = `<span class="block">${translatedDistributionLabel}</span><span class="block text-sm">${tableName}</span>`;
    }

    const items: ISimpleItem[] = [];
    items.push({
      icon: distributionIcon,
      value: distributionMode,
      label: distributionLabel,
    });

    const effectiveOrder =
      order ??
      (validatedOrder != null
        ? {
            estimated_delivery_duration: validatedOrder.estimated_delivery_duration,
            ready_at: this.normalizeDate(validatedOrder.ready_at),
          }
        : null);

    if (orderTypeCapabilities.displaySlot) {
      const wantedAtIcon = 'icon-clock';
      const wantedAtLabel =
        this.getWantedAtLabel(
          orderTypeCapabilities,
          wantedAt,
          distributionMode,
          effectiveOrder as DoodOrderModel | null,
          slots,
        ) ?? '';

      items.push({
        icon: wantedAtIcon,
        label: wantedAtLabel,
        value: wantedAtLabel,
      });
    }

    return items;
  }

  private getWantedAtLabel(
    orderTypeCapabilities: IOrderTypeCapabilities,
    wantedAt: Date | null,
    distributionMode: string,
    order?: DoodOrderModel | null,
    slots?: number[],
  ): string | undefined {
    const readyAtDate = this.normalizeDate(order?.ready_at);
    if (readyAtDate) {
      const defaultLabel = !wantedAt
        ? this.translateService.instant(ParametersWantedAtTypeLabels.ASAP)
        : this.translateService.instant(ParametersWantedAtTypeLabels.LATER);
      return this.getFormattedDate(readyAtDate, distributionMode, order, defaultLabel);
    }

    if (wantedAt) {
      const defaultLabel = dayjs(wantedAt).isToday()
        ? this.translateService.instant('parameters.wantedAt.today')
        : this.translateService.instant(ParametersWantedAtTypeLabels.LATER);

      return this.getFormattedDate(wantedAt, distributionMode, order, defaultLabel);
    }

    if (orderTypeCapabilities.preorderingAllowed && slots) {
      return this.getFormattedSlotLabel(slots, distributionMode, order);
    }

    if (!orderTypeCapabilities.preorderingAllowed) {
      return;
    }

    return ParametersWantedAtTypeLabels.ASAP;
  }

  private getFormattedDate(
    date: Date,
    distributionMode: string,
    order?: DoodOrderModel | null,
    defaultLabel = this.translateService.instant(ParametersWantedAtTypeLabels.LATER),
  ): string {
    const deltaSeconds =
      distributionMode === OrderTypeValues.Delivery
        ? (order?.estimated_delivery_duration ?? DEFAULT_DELIVERY_DURATION_SECONDS)
        : 0;
    const dateDisplayed = new Date(date.getTime() + deltaSeconds * 1000);
    const dateDayjs = DateUtils.dayjsInMarketplaceTimezone(dateDisplayed);

    const dateText = this.getDateText(dateDayjs, defaultLabel);

    return this.getFormattedLabel(dateText, date, distributionMode, order);
  }

  private getDateText(dateDayjs: Dayjs, defaultLabel: string): string {
    if (dateDayjs.isToday()) {
      return defaultLabel;
    } else if (dateDayjs.isTomorrow()) {
      return this.translateService.instant('parameters.wantedAt.tomorrow');
    } else {
      return DateUtils.formatLocalizedDate(
        dateDayjs.toDate(),
        this.translateService.instant('date.day-month-format'),
      );
    }
  }

  private getFormattedLabel(
    dateText: string,
    date: Date,
    distributionMode: string,
    order?: DoodOrderModel | null,
  ): string {
    const deltaSeconds =
      distributionMode === OrderTypeValues.Delivery
        ? (order?.estimated_delivery_duration ?? DEFAULT_DELIVERY_DURATION_SECONDS)
        : 0;
    const dateDisplayed = new Date(date.getTime() + deltaSeconds * 1000);

    return `<span class="block text-sm font-base">${dateText}</span> <span class="block font-base text-sm">${this.getReadyAtLabel(dateDisplayed)}</span>`;
  }

  private normalizeDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private getFormattedSlotLabel(
    slots: number[],
    distributionMode: string,
    order?: DoodOrderModel | null,
  ): string {
    if (!slots.length) {
      return `<span class="block text-sm font-base">${this.translateService.instant(ParametersWantedAtTypeLabels.ASAP)}</span>`;
    }
    const slotDayjs = dayjs.unix(slots[0]);
    const slotText = this.getDateText(
      slotDayjs,
      this.translateService.instant(ParametersWantedAtTypeLabels.ASAP),
    );
    const asapSlot = dayjs.unix(slots[0]).toDate();

    return this.getFormattedLabel(slotText, asapSlot, distributionMode, order);
  }

  private getReadyAtLabel(readyAt: Date): string | undefined {
    const minutes = DateUtils.dayjsInMarketplaceTimezone(readyAt).minute();
    const localizedHourFormat = this.translateService.instant('parameters.wantedAt.slots-format');
    if (minutes >= 0 && minutes < 15) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(0).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(15).format(localizedHourFormat)}`;
    } else if (minutes >= 15 && minutes < 30) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(15).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(30).format(localizedHourFormat)}`;
    } else if (minutes >= 30 && minutes < 45) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(30).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(45).format(localizedHourFormat)}`;
    } else if (minutes >= 45 && minutes < 60) {
      return `${DateUtils.dayjsInMarketplaceTimezone(readyAt).minute(45).format(localizedHourFormat)} - ${DateUtils.dayjsInMarketplaceTimezone(
        readyAt,
      )
        .hour(DateUtils.dayjsInMarketplaceTimezone(readyAt).hour() + 1)
        .minute(0)
        .format(localizedHourFormat)}`;
    }
  }
}
